"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReturnLockAcquiredResponse = exports.LinedAllocationRollup = exports.RECEIPT_LOCK_TTL_MS = exports.ReleaseReturnLockRequest = exports.AcquireReturnLockRequest = exports.UnlockReceiptRequest = exports.LockReceiptRequest = exports.ReceiptDetail = exports.ReturnLineAllocationRecord = exports.ReceiptSummary = exports.ReceiptFiscalBlock = exports.ReceiptLine = exports.ReceiptKind = exports.ReceiptLifecycleState = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * Returns + receipt lifecycle contracts (Sprint 10 — TRS §22).
 *
 * Terminals upload finalised transactions via `/sync/transactions`
 * (the Sprint 4 stub-501 flips to ready here). BOS persists them
 * in `receipts` with `lifecycleState = issued`. Subsequent refund
 * uploads referencing `originalReceiptId` insert
 * `return_line_allocations` ledger rows; cumulative coverage
 * advances the original receipt through `partially_returned` →
 * `fully_returned`.
 *
 * Soft-lock semantics (TRS §22.3): when a terminal starts a return
 * flow, it calls `POST /receipts/:id/acquire-return-lock` to set
 * `lockedByTerminalId` + `lockedUntil = now + 10 min`. Concurrent
 * acquirers see `409 RECEIPT_LOCKED`. Lock auto-expires.
 *
 * See `apps/bos/docs/modules/returns.md` for the full spec.
 */
exports.ReceiptLifecycleState = zod_1.z.enum([
    'issued',
    'partially_returned',
    'fully_returned',
    'locked',
    'expired',
]);
/**
 * The kind of receipt the row represents. Mirrors the
 * `receiptType` enum on `SyncTransactionRecord` (sync.ts) and the
 * `ReceiptType` enum on receipt-config (which keys layouts to
 * receipt kinds). Kept as a distinct enum here so persistence /
 * REST shapes don't transitively pull the sync envelope nor the
 * receipt-config layout structure.
 */
exports.ReceiptKind = zod_1.z.enum([
    'sale',
    'refund',
    'void',
    'parked',
    'x_report',
    'z_report',
    'copy',
]);
// ---------- Read shapes (admin REST surface) ----------
exports.ReceiptLine = zod_1.z.object({
    sku: zod_1.z.string().nullable(),
    name: zod_1.z.string().min(1),
    quantity: zod_1.z.number(),
    unitOfMeasure: zod_1.z.enum(['each', 'kg', 'g', 'l', 'ml']),
    lineTotalMinor: pricing_js_1.MoneyMinor,
    unitPriceMinor: pricing_js_1.MoneyMinor.nullable(),
    discountMinor: pricing_js_1.MoneyMinor.nullable(),
    discountType: zod_1.z.number().int().nullable().optional(),
    discountReason: zod_1.z.string().trim().min(1).max(254).nullable().optional(),
    taxCategory: zod_1.z.string(),
    fiscalTaxCategory: zod_1.z.string().trim().min(1).nullable().optional(),
    taxRatePercent: zod_1.z.number(),
});
exports.ReceiptFiscalBlock = zod_1.z.object({
    receiptNumber: zod_1.z.string().min(1),
    fiscalNumber: zod_1.z.string().min(1),
    timestampFormatted: zod_1.z.string().min(1),
    signature: zod_1.z.string().nullable(),
    qrPayload: zod_1.z.string().nullable(),
});
exports.ReceiptSummary = zod_1.z.object({
    id: zod_1.z.string(),
    clientUuid: zod_1.z.string(),
    issuingTerminalId: zod_1.z.string(),
    cashierId: zod_1.z.string().nullable(),
    shiftId: zod_1.z.string().nullable(),
    receiptType: exports.ReceiptKind,
    lifecycleState: exports.ReceiptLifecycleState,
    subtotalMinor: pricing_js_1.MoneyMinor,
    totalDiscountMinor: pricing_js_1.MoneyMinor.nullable(),
    totalMinor: pricing_js_1.MoneyMinor,
    countryPackId: zod_1.z.string().nullable(),
    countryPackVersion: zod_1.z.string().nullable(),
    originalReceiptId: zod_1.z.string().nullable(),
    /**
     * RL-513: who authorised the receipt for `cashierId` — the supervisor or
     * second cashier behind a refund. Null when nobody did; optional for a Back
     * Office that predates the fields.
     */
    approverCashierId: zod_1.z.string().nullable().optional(),
    approverEmployeeCode: zod_1.z.string().nullable().optional(),
    lockedByTerminalId: zod_1.z.string().nullable(),
    lockedAt: zod_1.z.string().datetime().nullable(),
    lockedUntil: zod_1.z.string().datetime().nullable(),
    finalisedAt: zod_1.z.string().datetime(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.ReturnLineAllocationRecord = zod_1.z.object({
    id: zod_1.z.string(),
    originalReceiptId: zod_1.z.string(),
    originalLineSku: zod_1.z.string(),
    originalLineIndex: zod_1.z.number().int().min(0),
    returnReceiptId: zod_1.z.string(),
    quantityReturned: zod_1.z.string(),
    refundAmountMinor: pricing_js_1.MoneyMinor,
    createdAt: zod_1.z.string().datetime(),
});
exports.ReceiptDetail = exports.ReceiptSummary.extend({
    lines: zod_1.z.array(exports.ReceiptLine),
    fiscal: exports.ReceiptFiscalBlock.nullable(),
    terminalLabel: zod_1.z.string().nullable(),
    cashierDisplayName: zod_1.z.string().nullable(),
    /** RL-513: the approver's display name, when BOS knows the approver. */
    approverDisplayName: zod_1.z.string().nullable().optional(),
    returnAllocations: zod_1.z.array(exports.ReturnLineAllocationRecord),
});
// ---------- Write shapes (admin REST surface) ----------
exports.LockReceiptRequest = zod_1.z
    .object({
    reason: zod_1.z.string().min(1).max(1000),
})
    .strict();
exports.UnlockReceiptRequest = zod_1.z.object({}).strict();
// ---------- Soft-lock acquire / release (terminal-driven) ----------
exports.AcquireReturnLockRequest = zod_1.z.object({}).strict();
exports.ReleaseReturnLockRequest = zod_1.z.object({}).strict();
/**
 * Default soft-lock TTL per TRS §22.3 (10 minutes). Exported so the
 * server-side reaper + terminal-side UX share the constant.
 */
exports.RECEIPT_LOCK_TTL_MS = 10 * 60 * 1000;
/**
 * Per-line allocation snapshot returned by `acquire-return-lock`
 * so the terminal can show remaining-eligible quantity per line
 * without a separate fetch.
 */
exports.LinedAllocationRollup = zod_1.z.object({
    lineIndex: zod_1.z.number().int().min(0),
    sku: zod_1.z.string().nullable(),
    originalQuantity: zod_1.z.number(),
    cumulativeReturnedQuantity: zod_1.z.string(),
    remainingEligibleQuantity: zod_1.z.string(),
});
exports.ReturnLockAcquiredResponse = zod_1.z.object({
    receipt: exports.ReceiptSummary,
    rollup: zod_1.z.array(exports.LinedAllocationRollup),
});
//# sourceMappingURL=returns.js.map